import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Upload, Trash2, Image as ImageIcon, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AIHealthWidget from "./AIHealthWidget";

const PACKAGES = ["Basic", "Classic", "Premium", "Luxury", "Custom"];
const OCCASIONS = ["Birthday", "Anniversary", "Baby Shower", "Engagement", "Housewarming", "Other"];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferenceGalleryTab = ({ authAxios }) => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filterPackage, setFilterPackage] = useState("");
  const [filterOccasion, setFilterOccasion] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPkg, setUploadPkg] = useState("Classic");
  const [uploadOcc, setUploadOcc] = useState("Birthday");
  const fileInputRef = useRef(null);

  const loadStats = async () => {
    try {
      const { data } = await authAxios.get("/api/admin/reference-gallery/stats");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load stats");
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPackage) params.package = filterPackage;
      if (filterOccasion) params.occasion = filterOccasion;
      const { data } = await authAxios.get("/api/admin/reference-gallery", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load gallery");
    }
    setLoading(false);
  };

  useEffect(() => { loadStats(); loadItems(); }, []);
  useEffect(() => { loadItems(); }, [filterPackage, filterOccasion]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("File too large (max 4MB)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("package", uploadPkg);
    fd.append("occasion", uploadOcc);
    fd.append("file", file);
    try {
      await authAxios.post("/api/admin/reference-gallery", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded to ${uploadPkg} / ${uploadOcc}`);
      loadStats(); loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reference image?")) return;
    try {
      await authAxios.delete(`/api/admin/reference-gallery/${id}`);
      toast.success("Deleted");
      loadStats(); loadItems();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reference-gallery-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1a1a2e]">Reference Gallery</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Fallback images shown on the Magic page when the AI is temporarily down.
            Upload real party photos per package + occasion. At least 1 per combo keeps conversion flowing during outages.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#F0DC00]/20 border border-[#F0DC00] text-[#1a1a2e] px-4 py-2 rounded-lg text-sm font-semibold">
          <CheckCircle size={16} /> Total images: {stats?.total ?? "…"}
        </div>
      </div>

      {/* Live AI health widget */}
      <AIHealthWidget authAxios={authAxios} />

      {/* Upload panel */}
      <Card className="border-2 border-dashed border-[#DC283C]/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#DC283C]">
            <Upload size={18} /> Upload reference image
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Package</Label>
              <select
                value={uploadPkg}
                onChange={(e) => setUploadPkg(e.target.value)}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                data-testid="upload-package-select"
              >
                {PACKAGES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label>Occasion</Label>
              <select
                value={uploadOcc}
                onChange={(e) => setUploadOcc(e.target.value)}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                data-testid="upload-occasion-select"
              >
                {OCCASIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label>Image (max 4MB, JPG/PNG)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                disabled={uploading}
                className="w-full mt-1 text-sm"
                data-testid="upload-file-input"
              />
              {uploading && <p className="text-xs text-[#DC283C] mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading…</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coverage matrix */}
      {stats && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Coverage matrix — images per bucket</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Package \ Occasion</th>
                    {stats.occasions.map(o => <th key={o} className="p-2 text-center">{o}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {stats.packages.map(pkg => (
                    <tr key={pkg} className="border-b last:border-0">
                      <td className="p-2 font-semibold">{pkg}</td>
                      {stats.occasions.map(occ => {
                        const n = stats.matrix[pkg]?.[occ] || 0;
                        const color = n === 0 ? "bg-red-100 text-red-700" : n < 2 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-700";
                        return (
                          <td key={occ} className="p-2 text-center">
                            <span className={`inline-block w-8 h-6 rounded ${color} font-bold flex items-center justify-center mx-auto`}>{n}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-2">
              <AlertTriangle size={12} className="text-red-600" /> Red = no fallback available for this combo (users see a plain error if AI is down).
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filter + gallery */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Filter:</Label>
        <select value={filterPackage} onChange={e => setFilterPackage(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-package">
          <option value="">All packages</option>
          {PACKAGES.map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={filterOccasion} onChange={e => setFilterOccasion(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-occasion">
          <option value="">All occasions</option>
          {OCCASIONS.map(o => <option key={o}>{o}</option>)}
        </select>
        <span className="text-xs text-gray-500">{items.length} image(s)</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed rounded-lg">
          <ImageIcon size={40} className="mx-auto mb-2 opacity-30" />
          No reference images yet. Upload some above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <div key={item.id} className="relative border rounded-xl overflow-hidden group shadow-sm hover:shadow-md transition-all">
              <img
                src={`${API_URL}${item.image_url}`}
                alt={`${item.package} ${item.occasion}`}
                className="w-full h-40 object-cover"
                data-testid="reference-image"
              />
              <div className="p-2 bg-white">
                <p className="text-xs font-semibold">{item.package} · {item.occasion}</p>
                <p className="text-[10px] text-gray-500 capitalize">{item.source} · {item.created_at?.slice(0, 10)}</p>
              </div>
              <button
                onClick={() => handleDelete(item.id)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete"
                data-testid={`delete-${item.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferenceGalleryTab;
